import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { axiosInstance } from '@/utils/lib/axios';
import { env } from '@/utils/constants/env';
import { toast } from 'react-toastify';

export interface IMediaItem {
  id: number;
  model_type: string;
  model_id: number;
  uuid: string;
  collection_name: string;
  name: string;
  file_name: string;
  mime_type: string;
  disk: string;
  conversions_disk: string | null;
  size: number;
  manipulations: Record<string, unknown>;
  custom_properties: Record<string, unknown>;
  generated_conversions: Record<string, unknown>;
  responsive_images: Record<string, unknown>;
  order_column: number | null;
  path: string;
  created_at: string;
  updated_at: string;
}

// Get media by model (response_id) and collection_name
export const useGetMediaByResponse = (
  tenantId: string | number | undefined,
  responseId: number | undefined,
  collectionName: 'documents' | 'images',
) => {
  return useQuery({
    queryKey: ['checklist-media', tenantId, responseId, collectionName],
    queryFn: async (): Promise<IMediaItem[]> => {
      if (!tenantId || !responseId) {
        return [];
      }

      try {
        const response = await axiosInstance.get<IMediaItem[]>(
          `${env.apiTasklistBaseUrl}/api/v1/compro/media/checklist_responses/${responseId}`,
          {
            params: { collection_name: collectionName },
          },
        );
        return response.data || [];
      } catch (error: unknown) {
        // Return empty array on error instead of throwing
        console.error('Error fetching media:', error);
        return [];
      }
    },
    enabled: !!tenantId && !!responseId,
    staleTime: 2 * 60 * 1000,
  });
};

// Upload media
export const useUploadMedia = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      responseId,
      collectionName,
      file,
    }: {
      responseId: number;
      collectionName: 'documents' | 'images';
      file: File;
    }) => {
      const formData = new FormData();
      formData.append('model_type', 'checklist_responses');
      formData.append('model_id', String(responseId));
      formData.append('collection_name', collectionName);
      formData.append('file', file, file.name);

      const response = await axiosInstance.post(`${env.apiTasklistBaseUrl}/api/v1/compro/media/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        transformRequest: [data => data], // Prevent axios from transforming FormData
      });

      return response.data;
    },
    onSuccess: (_, variables) => {
      // Invalidate all media queries for the specific response (both documents and images)
      queryClient.invalidateQueries({
        queryKey: ['checklist-media'],
        predicate: query => {
          const [, , responseId] = query.queryKey as [
            string,
            string | number | undefined,
            number | undefined,
            string | undefined,
          ];
          return responseId === variables.responseId;
        },
      });
      // Invalidate checklist instance to update media counts and included media data
      queryClient.invalidateQueries(['checklist-instance']);
      toast.success('File berhasil diunggah');
    },
    onError: (error: unknown) => {
      let errorMessage = 'Gagal mengunggah file';
      if (
        typeof error === 'object' &&
        error !== null &&
        'response' in error &&
        typeof error.response === 'object' &&
        error.response !== null &&
        'data' in error.response &&
        typeof error.response.data === 'object' &&
        error.response.data !== null &&
        'message' in error.response.data
      ) {
        errorMessage = String(error.response.data.message);
      }
      toast.error(errorMessage);
    },
  });
};

// Delete media by ID
export const useDeleteMedia = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ mediaId }: { mediaId: number }) => {
      const response = await axiosInstance.delete(`${env.apiTasklistBaseUrl}/api/v1/compro/media/${mediaId}`);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate all media queries (will refetch both documents and images)
      queryClient.invalidateQueries({ queryKey: ['checklist-media'] });
      // Invalidate checklist instance to update media counts and included media data
      queryClient.invalidateQueries({ queryKey: ['checklist-instance'] });
      toast.success('File berhasil dihapus');
    },
    onError: (error: unknown) => {
      let errorMessage = 'Gagal menghapus file';
      if (
        typeof error === 'object' &&
        error !== null &&
        'response' in error &&
        typeof error.response === 'object' &&
        error.response !== null &&
        'data' in error.response &&
        typeof error.response.data === 'object' &&
        error.response.data !== null &&
        'message' in error.response.data
      ) {
        errorMessage = String(error.response.data.message);
      }
      toast.error(errorMessage);
    },
  });
};
